// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { authApi } from '../api/auth';
import { ApiRequestError } from '../api/client';
import type { AuthMe } from '../api/types';
import { ErrorBanner } from '../components/ErrorBanner';
import { browserSessionStorage, browserStorage, clearViewerStorage } from '../components/bygning/settings/settingsStorage';
import { Spinner } from '../components/Spinner';
import { authBootAction, LOGIN_PATH } from './auth';
import { readShareToken, rememberShareToken } from './cases';
import { sessionBridge } from './sessionRuntime';
import { setLoginRedirect } from './unauthorized';
import styles from './AuthGate.module.css';

export interface AuthState {
  me: AuthMe;
  /** Ends the session and goes to the login page (server mode only). */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Who is signed in. Only inside an AuthGate. */
export function useAuth(): AuthState | null {
  return useContext(AuthContext);
}

/**
 * Asks the server who this browser is before anything else renders. Signed
 * out (401): on to the login page, which comes back here. In local mode the
 * server answers with its implicit user, so the login page never shows.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AuthMe | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    const proceed = (answer: AuthMe) => {
      // A real sign-in ends any share-link visit this tab remembered.
      if (answer.via !== 'share_link') rememberShareToken(null);
      sessionBridge()?.observe(answer);
      setLoginRedirect(answer.mode === 'server');
      setMe(answer);
    };
    const fail = (cause: unknown) => {
      const action = authBootAction(
        { ok: false, status: cause instanceof ApiRequestError ? cause.status : undefined },
        window.location.pathname + window.location.search,
        readShareToken(),
      );
      if (action.kind === 'login') window.location.replace(action.href);
      else setError(cause instanceof Error ? cause : new Error(String(cause)));
    };
    authApi.me(controller.signal).then(
      (answer) => {
        if (!controller.signal.aborted) proceed(answer);
      },
      async (cause: unknown) => {
        if (controller.signal.aborted) return;
        // ruxd's session is gone, but a Supabase one may still be good:
        // trade it for a fresh cookie before sending anyone to the login page.
        const bridge = sessionBridge();
        if (bridge && cause instanceof ApiRequestError && cause.status === 401) {
          try {
            const answer = await bridge.rebridge();
            if (controller.signal.aborted) return;
            if (answer) return proceed(answer);
          } catch (bridgeCause) {
            if (!controller.signal.aborted) fail(bridgeCause);
            return;
          }
        }
        fail(cause);
      },
    );
    return () => controller.abort();
  }, [attempt]);

  const logout = useCallback(async () => {
    // The 3D viewer's remembered settings belong to this person (frontend-38f).
    clearViewerStorage(browserStorage(), browserSessionStorage());
    try {
      const bridge = sessionBridge();
      if (bridge) await bridge.signOut();
      else await authApi.logout();
    } finally {
      window.location.assign(LOGIN_PATH);
    }
  }, []);

  if (error)
    return (
      <div className={styles.wait}>
        <ErrorBanner error={error} onRetry={() => setAttempt((n) => n + 1)} context="login-oplysningerne" />
      </div>
    );
  if (!me)
    return (
      <div className={styles.wait}>
        <Spinner label="Tjekker login…" />
      </div>
    );
  return <AuthContext.Provider value={{ me, logout }}>{children}</AuthContext.Provider>;
}
